import { useAtom } from 'jotai'
import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'

import { Profile } from '../../../models/Profile'
import { getInitials } from '../../../utils/initials'

import { getProfileAtom, profileAtom } from '../../../api/profile/profile'
import { colors } from '../../../theme/palette'
import { CircleText, CircleIcon2 } from '../../ui/Circle/Circle'
import { CardColumn, Row } from '../../ui/Card/CardColumn'
import { Typography } from '../../ui/Typography/Typography'
import { baseButton } from '../../../theme/reuseables'
import {
    CheckFill,
    Foa,
    PersonalInfo,
    ContactLedger,
    Envelop,
} from '../../../assets/icons'
import { CardColumnContainer } from '../../ui/Card/CardColumnContainer'
import { formatDate } from '../../../utils/format'
import { EditMyProfile } from '../../modules/EditMyProfile'
import { Workplace } from '../../../models/Workplace'
import { TypeOfRepresentative } from '../../../models/TypeOfRepresentative'
import { L, L_HTML } from '../../providers/Lokalise'
import { Button } from '../../ui/Button/Button'
import { Member } from '../../../models/Member'
import { WorkplaceDropdown } from '../../modules/WorkplaceDropdown'
import breakpoints from '../../../theme/breakpoints'
import { getSuppleantNamesForTR, isSuppleantFor } from '../../../utils/utils'
import { ImitatorNoAccessWarning } from '../../modules/Imitator/ImitatorNoAccessWarning'
import { OrganizationWidget } from '../../modules/OrganizationWidget'

const useStyles = createUseStyles({
    container: {
        color: colors.black,
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                gridTemplateColumns: '1fr 1fr 1fr',
                padding: '0 0 5rem',
            },
        },
    },
    profileHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '3rem 0',
    },
    nameContainer: {
        display: 'flex',
        flexBasis: '50%',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    edit: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...baseButton,
        ...{
            cursor: 'pointer',
            margin: '0',
        },
    },
    section: {
        marginBottom: '3rem',

        '& > hr': {
            opacity: '0.2',
            margin: '1.25rem 0',
        },
    },
    iconWrapper: {
        position: 'relative',
        width: '90px',
        marginRight: '30px',
    },
    buttons: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        '& button': {
            margin: '10px 0',
            width: '100%',
        },
    },
    editIconWrapper: {
        top: '0',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'end',
    },
    confirmButtons: {
        display: 'flex',
        zIndex: '1',
        position: 'relative',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        padding: '20px 0 0',
    },
})

export const buildRow = (
    value: string | number,
    label: string,
    mailToLinkIcon?: JSX.Element
): Row => {
    return {
        label,
        value,
        mailToLinkIcon,
    }
}

const MyProfile: React.FC = () => {
    const classes = useStyles()
    const [data, update] = useAtom(getProfileAtom)
    const [authProfile] = useAtom(profileAtom)
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        Workplace | undefined
    >(undefined)
    const [showImitatorModal, setShowImitatorModal] = useState(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calcPercentageCovered = (
        numberFoaMemberUserCovers: number,
        totalNumberOfPeopleUserCovers: number
    ): number => {
        return (numberFoaMemberUserCovers / totalNumberOfPeopleUserCovers) * 100
    }

    // TODO add Labels from sitecore
    const getPersonalInfoRows = (d: Profile): Row[] => {
        return [
            buildRow(
                formatDate(new Date(d.personalInfo.birthdate)),
                L('GENERISK_FORM_FØDSELDAG_LABEL')
            ),
            buildRow(
                d.personalInfo.age ? `${d.personalInfo.age} år` : '-',
                L('GENERISK_FORM_ALDER_LABEL')
            ),
        ]
    }

    const getMembershipInfoRows = (d: Profile): Row[] => {
        return [
            buildRow(d.memberNumber, L('GENERISK_FORM_MEDLEMSNUMMER_LABEL')),
            buildRow(
                d.membershipInfo.name,
                L('GENERISK_FORM_FOAAFDELING_LABEL')
            ),
            buildRow(
                formatDate(new Date(d.membershipInfo.date)),
                L('GENERISK_FORM_INDMELDELSESDATO_LABEL')
            ),
        ]
    }

    const getContactInfoRows = (d: Profile): Row[] => {
        const noShowMessage = d.privateContact?.isTelefonPrivate
            ? 'Hemmeligt nummer'
            : '-'
        return [
            buildRow(
                d.privateContact ? d.privateContact.mobileShow : noShowMessage,
                L('TRPROFIL_MOBIL_LABEL')
            ),
            buildRow(
                d.privateContact ? d.privateContact.email : 'N/A',
                L('TRPROFIL_EMAIL_LABEL'),
                d.privateContact && d.privateContact.email && (
                    <a href={`mailto:${d.contactInfoList[0].email}`}>
                        <Envelop color={colors.green} width="40px" />
                    </a>
                )
            ),
        ]
    }

    const getDuties = (member: Member, profile: Profile): string => {
        const duties = []

        member.allWorkPlaces.forEach((w) => {
            if (w.typeOfRepresentatives && w.typeOfRepresentatives.length) {
                w.typeOfRepresentatives.forEach((t) => {
                    const found = profile.typeOfRepresentatives.find(
                        (tor) => tor.type === t.supplementedOfDutyType
                    )

                    if (found) {
                        duties.push(found.name)
                    }
                })
            }
        })
        return [...new Set(duties)].join(',') || ''
    }

    const getSuppleantForTRRows = (d: Profile): JSX.Element => {
        if (d.trForSelf.length === 1) {
            return (
                <CardColumn
                    titleIcon={<CheckFill />}
                    titleLabel={L('TRPROFIL_MINE-SUPPLEANTER_TITEL')}
                    rows={[
                        buildRow(
                            d.trForSelf[0].fullName,
                            L('TRPROFIL_MINE-SUPPLEANTER_NAVN')
                        ),
                        buildRow(
                            getDuties(d.trForSelf[0], d),
                            L('TRPROFIL_MINE-SUPPLEANTER_TILLIDHVERV')
                        ),
                    ]}
                    key="Mine suppleanter"
                />
            )
        }

        return (
            <>
                <CardColumn
                    titleIcon={<CheckFill />}
                    titleLabel="Mine suppleanter"
                    rows={[]}
                    key="Mine suppleanter"
                />
                {d.trForSelf.map((tr) => {
                    return (
                        <CardColumn
                            hideTitle
                            titleIcon={<CheckFill />}
                            titleLabel={L('TRPROFIL_HVERV_TITEL')}
                            rows={[
                                buildRow(tr.fullName, L('TRPROFIL_HVERV_NAVN')),
                                buildRow(
                                    getDuties(tr, d),
                                    L('TRPROFIL_HVERV_TILLIDSHVERV')
                                ),
                            ]}
                            key={`${tr.memberId}${tr.memberNumber}`}
                        />
                    )
                })}
            </>
        )
    }

    const getTypeOfRepresentativeRows = (d: TypeOfRepresentative): Row[] => {
        const rows = [
            buildRow(
                // eslint-disable-next-line max-len
                `${d?.name}(${d?.shortName})`,
                L('GENERISK_FORM_TILLIDSHVERV_LABEL')
            ),
            buildRow(
                d ? formatDate(new Date(d.electedDate)) : 'N/A',
                L('GENERISK_FORM_GENVALGSDATO_LABEL')
            ),
        ]

        if (isSuppleantFor(d.type)) {
            rows.push(
                buildRow(
                    getSuppleantNamesForTR(d.type, data),
                    L('GENERISK_FORM_SUPPLEANT_FOR_LABEL')
                )
            )
        }

        return rows
    }

    const buildTor = (tors: TypeOfRepresentative[]): JSX.Element => {
        return (
            <>
                {tors
                    .sort((a, b) => {
                        if (a.sort > b.sort) return 1
                        if (a.sort < b.sort) return -1
                        return 0
                    })
                    .map((tor) => {
                        return (
                            <CardColumn
                                hideTitle
                                titleIcon={<CheckFill />}
                                titleLabel="Hverv"
                                rows={getTypeOfRepresentativeRows(tor)}
                                key={`${tor.type}${tor.sort}`}
                            />
                        )
                    })}
            </>
        )
    }

    useEffect(() => {
        setSelectedWorkplace(data.primaryWorkPlace)
    }, [])

    if (!data) return null

    return (
        <div className={`${classes.container} page-up`}>
            <div className={classes.profileHeader}>
                <div className={classes.nameContainer}>
                    <div className={classes.iconWrapper}>
                        <CircleIcon2 width="92px" withBorder borderWidth="6px">
                            <CircleText fontSize="30px">
                                {getInitials(data.fullName)}
                            </CircleText>
                        </CircleIcon2>
                    </div>
                    <Typography tag="h1" size="XXXXL" spacing=".2rem">
                        {data.fullName}
                    </Typography>
                </div>
                {data ? <EditMyProfile profile={data} /> : null}
            </div>
            <div className={classes.section}>
                <hr />
                <CardColumnContainer>
                    <div>
                        <CardColumn
                            titleIcon={<PersonalInfo />}
                            titleLabel={L(
                                'TRPROFIL_DETALJE_PERSONLIGEOPLYSNINGER-TITEL'
                            )}
                            rows={getPersonalInfoRows(data)}
                            key="Personlige oplysninger"
                        />
                        <CardColumn
                            titleIcon={<Foa />}
                            titleLabel={L(
                                'TRPROFIL_DETALJE_FOAMEDLEMSSKAB-TITEL'
                            )}
                            rows={getMembershipInfoRows(data)}
                            key="FOA medlemsskab"
                        />
                    </div>
                    {selectedWorkplace ? (
                        <WorkplaceDropdown
                            showHo={false}
                            workplaces={data.workPlaces}
                            selectedWorkplace={selectedWorkplace}
                            profile={data}
                            setSelectedWorkplace={setSelectedWorkplace}
                            beforContent={
                                <div>
                                    <Typography
                                        size="XS"
                                        style={{ marginBottom: '20px' }}
                                    >
                                        {L_HTML('RET_OPLYSNINGER_PROFIL_TEXT')}
                                    </Typography>
                                </div>
                            }
                        />
                    ) : null}
                    {data.contactInfoList && data.contactInfoList.length ? (
                        <CardColumn
                            iconWidth="16px"
                            titleIcon={<ContactLedger />}
                            titleLabel={L('TRPROFIL_DETALJE_KONTAKT-TITEL')}
                            rows={getContactInfoRows(data)}
                            key="Kontakt"
                        />
                    ) : null}
                </CardColumnContainer>
            </div>
            <div className={classes.section}>
                {/* <Typography tag="h2" size="L" spacing=".2rem">
                    {L('GENERISK_FORM_TILLIDSHVERV_LABEL')}
                </Typography> */}
                <hr />
                <CardColumnContainer>
                    <div>
                        <CardColumn
                            titleIcon={<CheckFill />}
                            titleLabel="Hverv"
                            rows={[
                                {
                                    label: '',
                                    value: '',
                                    useContent: true,
                                    content: (
                                        <>
                                            {
                                                <div
                                                    className={classes.buttons}
                                                >
                                                    <Button
                                                        style={{
                                                            wordBreak:
                                                                'break-word',
                                                        }}
                                                        size="large"
                                                        onClick={() =>
                                                            window.open(
                                                                // eslint-disable-next-line max-len
                                                                process.env
                                                                    .REACT_APP_LINK_BLANKET_OPHØR_TRHVERV,
                                                                '_blank'
                                                            )
                                                        }
                                                    >
                                                        {L_HTML(
                                                            'TRPROFIL_BLANKET_RET_OPHØR-HVERV_LABEL'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        style={{
                                                            wordBreak:
                                                                'break-word',
                                                        }}
                                                        size="large"
                                                        onClick={() =>
                                                            window.open(
                                                                // eslint-disable-next-line max-len
                                                                process.env
                                                                    .REACT_APP_LINK_BLANKET_OPDATER_TRHVERV,
                                                                '_blank'
                                                            )
                                                        }
                                                    >
                                                        {L_HTML(
                                                            'TRPROFIL_BLANKET_RET_OPDATER-HVERV_LABEL'
                                                        )}
                                                    </Button>
                                                </div>
                                            }
                                        </>
                                    ),
                                },
                            ]}
                        />
                        {buildTor(data.typeOfRepresentatives)}
                    </div>

                    <>
                        {data.trForSelf.length ? (
                            <div>{getSuppleantForTRRows(data)} </div>
                        ) : null}
                        {/* {data.trForOthers.length ? (
                            <div>{getAlternateRepresentativeRows(data)} </div>
                        ) : null} */}
                        {/* {data.trForSelf.length
                            ? getSuppleantForTRRows(data)
                            : null} */}
                    </>
                    <CardColumn
                        titleIcon={<PersonalInfo />}
                        titleLabel={L(
                            'TRPROFIL_ORGANISERINGSWIDGET_OVERSKRIFT'
                        )}
                        rows={[
                            {
                                label: '',
                                value: '',
                                useContent: true,
                                content: (
                                    <OrganizationWidget
                                        data={{
                                            numberFoaMemberUserCoversTR:
                                                data.numberFoaMemberUserCoversTR,
                                            numberFoaMemberUserCoversFTR:
                                                data.numberFoaMemberUserCoversFTR,
                                            totalNumberOfPeopleUserCoversTR:
                                                data.totalNumberOfPeopleUserCoversTR,
                                            totalNumberOfPeopleUserCoversFTR:
                                                data.totalNumberOfPeopleUserCoversFTR,
                                        }}
                                        authProfile={authProfile}
                                        onUpdate={() =>
                                            update({ type: 'refetch' })
                                        }
                                        onImitatorModal={() =>
                                            setShowImitatorModal(true)
                                        }
                                    />
                                ),
                            },
                        ]}
                    />
                </CardColumnContainer>
            </div>
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </div>
    )
}

export { MyProfile }
